import React, { useState, useEffect } from "react";
import { generateBillAction, productDropdownAction } from "../../redux/actions/billingAction";
import { useDispatch } from "react-redux";
import ProductCard from "../../components/productCard";
import OrderSummary from "../../components/orderSummary";
import InnerLayout from "../../components/layouts/innerLayout";
import search from "../../assets/icons/searchIcon.svg";
import { useNavigate } from "react-router-dom";
import { errorToast } from "../../components/toast";
import { accountDetailsAction } from "../../redux/actions/authAction";
import LoaderModal from "../../components/modals/loaderModal";
import { handleThermalPrint } from "../../components/helper";

const ProductBilling = () => {
    const [products, setProducts] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [accountData, setAccountData] = useState(null);
    const [payment, setPayment] = useState(null)
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // const [isDrawerOpen, setIsDrawerOpen] = useState(true);


    useEffect(() => {
        fetchAccount()
        fetchProducts()
    }, []);

    const fetchProducts = () => {
        setLoading(true)
        dispatch(productDropdownAction({}, onProdFetchSuccess, onError))
    }

    const onProdFetchSuccess = (data) => {
        const updatedProds = data.data.data.map((prod) => {
            let obj = {
                id: prod.id,
                name: prod.name,
                image: prod.image,
                price: prod?.price,
                tax: prod?.tax,
                discount: prod?.discount,
                hsnCode: prod?.hsnCode,
                barcode: prod?.barcode,
                quantity: 0
            }
            return obj;
        });
        console.log(updatedProds, "prod");

        setProducts(updatedProds);
        setLoading(false)
    }

    const fetchAccount = () => {
        setLoading(true)
        dispatch(accountDetailsAction({}, (data) => { setAccountData(data.data.data) }, onError))
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
        setLoading(false)
    }


    const calculatePriceDetails = (price, tax, discount, taxOnProduct, gstEnabled, transactionIn, quantity) => {
        // Parse inputs to ensure proper calculation
        price = parseFloat(price) || 0;
        tax = gstEnabled ? parseFloat(tax) || 0 : 0;
        discount = parseFloat(discount) || 0;
        quantity = parseInt(quantity, 10) || 1;

        // Calculate discounted price
        const discountedPrice = price - (price * discount / 100);

        let cgst = 0, sgst = 0, igst = 0, totalTax = 0, productPrice = discountedPrice;

        if (gstEnabled) {
            if (taxOnProduct === "inclusive") {
                // Tax-inclusive: Calculate the base price (price without tax)
                productPrice = discountedPrice / (1 + (tax / 100));
                totalTax = discountedPrice - productPrice;

            } else {
                // Tax-exclusive: Calculate tax based on the discounted price
                productPrice = discountedPrice;
                totalTax = (discountedPrice * tax) / 100;
            }

            // Split the tax based on transaction type
            if (transactionIn === "intraState") {
                cgst = totalTax / 2;
                sgst = totalTax / 2;
            } else if (transactionIn === "interState") {
                igst = totalTax;
            }
        }

        // Calculate unit price (base price + total tax) and total price
        const unitPrice = productPrice + totalTax;
        const totalPrice = unitPrice * quantity;

        return {
            price,
            discount,
            discountedPrice: discountedPrice.toFixed(2),
            tax,
            cgst: cgst.toFixed(2),
            sgst: sgst.toFixed(2),
            igst: igst.toFixed(2),
            totalTax: totalTax.toFixed(2),
            productPrice: productPrice.toFixed(2),
            unitPrice: unitPrice.toFixed(2),
            totalPrice: totalPrice.toFixed(2),
        };
    }

    const handleProductClick = (product) => {
        setSelectedItems((prev) => {
            const existing = prev.find((p) => p.id === product.id);
            const price = parseFloat(product.price) || 0;
            const discount = parseFloat(product.discount) || 0;
            const taxRate = parseFloat(product.tax) || parseFloat(accountData.default_tax) || 0;
            const quantity = existing ? existing.quantity + 1 : 1;

            const details = calculatePriceDetails(
                price,
                taxRate,
                discount,
                accountData.tax_with_product,
                accountData.gstEnabled,
                accountData.transaction_in,
                quantity
            );

            const enrichedProduct = {
                ...product,
                ...details,
                quantity,
            };

            if (existing) {
                return prev.map((p) =>
                    p.id === product.id ? enrichedProduct : p
                );
            }

            return [...prev, enrichedProduct];
        });
    };

    const handleQuantityChange = (id, change) => {
        setSelectedItems((prev) =>
            prev
                .map((p) => {
                    if (p.id !== id) return p;

                    const newQty = p.quantity + change;
                    if (newQty <= 0) return null;

                    const details = calculatePriceDetails(
                        p.price,
                        p.tax,
                        p.discount,
                        accountData.tax_with_product,
                        accountData.gstEnabled,
                        accountData.transaction_in,
                        newQty
                    );

                    return {
                        ...p,
                        ...details,
                        quantity: newQty,
                    };
                })
                .filter(Boolean)
        );
    };

    const handleCancel = () => setSelectedItems([]);
    const handleSubmit = () => {
        if (!payment) {
            errorToast("Please select payment method")
            return
        }
        const dispathValues = {
            customerPhone: '',
            customerName: '',
            payment_method: payment,
            products: selectedItems
        }
        console.log("Billed prods", dispathValues)
        if (selectedItems.length < 1) {
            errorToast("Atleast one product required")
        } else {
            dispatch(generateBillAction(dispathValues, onSuccess, onError))
        }
    };

    const onSuccess = (data) => {
        if (accountData?.printPreference === "thermal") {
            handleThermalPrint(data.data.data, accountData);
            setSelectedItems([])
            setPayment(null)

        } else {
            navigate('/invoice', { state: { invoiceData: data.data.data } })
        }
    }

    const handleSearch = (e) => setSearchTerm(e.target.value.toLowerCase());

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm)
    );

    return (
        <InnerLayout module={'Billing'}>
            <div className="flex flex-wrap w-full">
                <div className={`w-full lg:w-3/4 md:h-full h-[35rem] md:overflow-visible overflow-auto`}>
                    <div className="sticky top-0 bg-gray-100 p-2">
                        <div
                            style={{ height: "2.5rem" }}
                            className={`input_border flex items-center rounded-md w-full sm:w-auto mx-1 md:w-1/3 `}
                        >
                            <span className="px-2">
                                <img width={"16px"} src={search} alt="search icon" />
                            </span>
                            <input
                                placeholder={`Search here`}
                                className="table_search_input w-full rounded-md placeholder:text-sm py-1 px-2"
                                type="text"
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <span className='fs_14 mx-3 cursor-pointer text-primary' onClick={() => setSearchTerm('')} >Clear</span>
                        </div>
                    </div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {filteredProducts.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onClick={() => handleProductClick(product)}
                            />
                        ))}
                    </div>
                </div>
                {/* {selectedItems.length > 0 && (
                    <button
                        onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                        className="md:hidden fixed bottom-0 left-[53%] transform -translate-x-1/2 bg-primary text-white px-6 py-2 rounded-t-md z-50 shadow-md transition-all duration-300 ease-in-out"
                        style={{ marginBottom: isDrawerOpen ? '54vh' : '0' }} // Adjust margin when drawer is open
                    >
                        {isDrawerOpen ? 'Close' : 'View / Print'}
                    </button>
                )} */}

                <OrderSummary
                    items={selectedItems}
                    onQuantityChange={handleQuantityChange}
                    onCancel={handleCancel}
                    onSubmit={handleSubmit}
                    setPayment={setPayment}
                    payment={payment}
                />
            </div>
            {loading &&
                <LoaderModal />
            }
        </InnerLayout>
    );
};

export default ProductBilling;
